import React, { useState, useMemo } from 'react';
import { useTranslation } from "react-i18next";
import { useDroppable } from '@dnd-kit/core';
import { checkOverlapWithRotation } from '../configuratorUtils';
import { GRID_CELL_SIZE_M } from '../configuratorConstants';

const PlacementSlotRenderer = ({ slot, scale, onClick, ghostModulePosition, savedModuleForMove, moduleToMove }) => {
    const { t } = useTranslation();
    const { x, y, rotation, width, height } = slot;
    const [isHovered, setIsHovered] = useState(false);

    const { isOver, setNodeRef } = useDroppable({
        id: `slot-${slot.id}`,
        data: { slot },
    });

    const isGhostOverSlot = useMemo(() => {
        if (!ghostModulePosition || !savedModuleForMove || !moduleToMove) return false;
        
        const ghostModule = {
            ...savedModuleForMove,
            x: ghostModulePosition.x,
            y: ghostModulePosition.y,
            rotation: savedModuleForMove.rotation || 0,
            width: (moduleToMove.config?.cellsWide || savedModuleForMove.cellsWide) * GRID_CELL_SIZE_M,
            height: (moduleToMove.config?.cellsLong || savedModuleForMove.cellsLong) * GRID_CELL_SIZE_M,
        };

        const slotModule = {
            x: slot.x,
            y: slot.y,
            rotation: slot.rotation || 0,
            width: slot.width,
            height: slot.height,
        };

        return checkOverlapWithRotation(ghostModule, slotModule);
    }, [ghostModulePosition, savedModuleForMove, moduleToMove, slot]);

    const slotWidthPx = width * scale;
    const slotHeightPx = height * scale;
    
    const transform = `translate(${x * scale}, ${y * scale}) rotate(${rotation || 0})`;
    
    const fontSize = Math.max(12, 0.3 * scale);

    const textTransform = `rotate(${-rotation || 0}, ${slotWidthPx / 2}, ${slotHeightPx / 2})`;

    const shouldHighlight = isOver || isHovered || isGhostOverSlot;

    const rectStyle = {
        fill: shouldHighlight ? 'rgba(52, 211, 153, 0.25)' : 'rgba(199, 29, 59, 0.1)',
        stroke: shouldHighlight ? 'rgba(5, 150, 105, 0.9)' : 'rgba(199, 29, 59, 0.6)',
        transition: 'fill 0.2s ease, stroke 0.2s ease',
    };

    const textFill = shouldHighlight ? '#059669' : '#C71D3B';

    const handleClick = (e) => {
        e.stopPropagation();
        e.preventDefault();
        if (onClick) {
            onClick(slot);
        }
    };

    const handleMouseEnter = () => {
        setIsHovered(true);
    };

    const handleMouseLeave = () => {
        setIsHovered(false);
    };

    return (
        <g 
            ref={setNodeRef}
            transform={transform} 
            className="cursor-pointer group"
            onClick={handleClick}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <rect
                x={0}
                y={0}
                width={slotWidthPx}
                height={slotHeightPx}
                strokeWidth={shouldHighlight ? Math.max(1.5, 0.03 * scale) : Math.max(1, 0.02 * scale)}
                rx={Math.max(4, 0.08 * scale)}
                strokeDasharray="4, 4"
                className="transition-all duration-150"
                style={rectStyle}
            />
            <text
                x={slotWidthPx / 2}
                y={slotHeightPx / 2}
                fill={textFill}
                fontSize={fontSize}
                fontWeight="500"
                textAnchor="middle"
                dominantBaseline="middle"
                pointerEvents="none"
                className="select-none transition-colors"
                transform={textTransform}
            >
                {t('renderers.addHere')}
            </text>
        </g>
    );
};

export default React.memo(PlacementSlotRenderer);